<template>
	<article class="resource resource-video">
		<PageTitleComponent />

		<div class="container-expanded video-container">
			<YoutubeComponent :id="youtubeId" />
		</div>

		<div class="resource-content">
			<div class="main">
				<div class="title-container">
					<div class="title">
						<ResourceSeriesComponent :resource="resource" />
						<h1>{{ resource.post_title }}</h1>
					</div>
					<ResourceActionsComponent :resource="resource" />
				</div>
				<div
					v-if="resource.post_excerpt"
					class="summary"
				>
					<div
						class="content"
						v-html="resource.post_excerpt"
					/>
				</div>

				<div
					v-if="resource.post_content"
					class="post-content content"
					v-html="resource.post_content"
				/>

				<div
					v-if="resource.transcript"
					class="transcript"
					:class="{ 'transcript-open': transcriptOpen }"
				>
					<div
						class="transcript-header"
						role="button"
						@click="transcriptOpen = !transcriptOpen"
					>
						<h3>Transcript</h3>
						<div class="carat">
							<CaratIconComponent />
						</div>
					</div>
					<div class="transcript-body">
						<div
							class="transcript-body-content"
							v-html="resource.transcript"
						/>
					</div>
				</div>
			</div>
			<aside v-if="previousResources.length">
				<div class="previous-resources">
					<h3>Other videos in this series</h3>
					<ul class="no-style">
						<li
							v-for="r in previousResources"
							:key="r.ID"
							:class="{
								current: r.ID === resource.ID,
							}"
						>
							<ResourcesListItemComponent
								:hide-actions="true"
								:resource="r"
								:small="true"
								:current="true"
							/>
						</li>
					</ul>
				</div>
			</aside>
		</div>
	</article>
</template>

<script lang="ts">
import { type PropType, defineComponent, watch } from 'vue';
import { request } from '@/services/requestService';
import ResourceActionsComponent from '@/components/resources/ResourceActionsComponent.vue';
import ResourceSeriesComponent from '@/components/resources/ResourceSeriesComponent.vue';
import PageTitleComponent from '@/components/shared/PageTitleComponent.vue';
import CaratIconComponent from '@/images/carat.svg?component';
import ResourcesListItemComponent from '@/components/resources/ResourcesListItemComponent.vue';
import { resourceAudienceKeys } from '@/interfaces/resourceFilterInterface';
import type { Resource } from '@/interfaces/resourceInterface';
import YoutubeComponent from '@/components/shared/YoutubeComponent.vue';

export default defineComponent({
	components: { ResourceActionsComponent, PageTitleComponent, CaratIconComponent, ResourcesListItemComponent, ResourceSeriesComponent, YoutubeComponent },
	props: {
		resource: {
			type: Object as PropType<Resource>,
			required: true,
		},
	},
	data() {
		return {
			transcriptOpen: false,
			previousResources: [] as Resource[],
		};
	},
	computed: {
		isParentVideo() {
			return this.resource.audience === resourceAudienceKeys.Parent;
		},
		youtubeId() {
			// Stored as the bare 11-char video ID (normalised on save in the backend).
			return this.resource.youtube_id;
		},
		youtubeimgsrc(): string {
			// https://stackoverflow.com/questions/2068344/how-do-i-get-a-youtube-video-thumbnail-from-the-youtube-api
			return this.youtubeId ? `url('https://img.youtube.com/vi/${this.youtubeId}/hqdefault.jpg')` : '';
		},
		youtubeLink(): string {
			return this.youtubeId ? `https://www.youtube.com/watch?v=${this.youtubeId}` : '';
		},
	},
	beforeMount() {
		if (this.isParentVideo) {
			watch(() => this.$route.params.slug, this.getPreviousParentVideos, {
				immediate: true,
			});
		}
	},
	methods: {
		async getPreviousParentVideos() {
			const res = await request<{ resources: Resource[] }>('publicResource', 'getRelatedParentVideos', {
				resourceId: this.resource.ID,
			});

			if (res.success) {
				this.previousResources = res.data.resources;
			}
		},
	},
});
</script>

<style scoped lang="scss">
@use '@/styles/utils';

.resource {
	.aspect-ratio-box {
		padding-top: calc(315 / 560 * 100%);

		// don't display none this so that this image loads initially
		.link {
			/* stylelint-disable-next-line declaration-property-value-no-unknown */
			background-image: v-bind(youtubeimgsrc);
			background-position: center;
			background-size: cover;
			background-repeat: no-repeat;
		}

		iframe {
			width: 100%;
			height: 100%;
		}
	}

	h1 {
		margin-bottom: var(--rs-layout-content-padding);
		color: rgb(var(--rs-color-brand-cherry));
		font-size: 3.6rem;
		font-family: var(--rs-typography-font-heading);
	}

	.resource-content {
		display: flex;
		flex-wrap: wrap;
		gap: 40px;
		margin: 30px 0 20px 0;

		.main {
			max-width: 680px;

			.title-container {
				margin-bottom: 30px;

				.actions {
					margin-top: 20px;
					margin-left: auto;
					padding-left: 20px;
				}
			}

			.title {
				position: relative;

				.resource-order {
					margin-bottom: 2px;
					font-size: 1.25rem;
				}

				h1 {
					margin-bottom: 0;
				}
			}
		}

		aside {
			flex: 1;
			margin-top: 20px;
			width: 100%;

			h3 {
				margin-top: -4px;
				margin-bottom: 13px;
			}
		}
	}

	h3 {
		color: rgb(var(--rs-color-brand-grey-text));
		font-size: 2rem;
	}

	.summary {
		h3 {
			margin-bottom: 15px;
		}

		.content {
			font-size: 2.2rem;
		}
	}

	.post-content {
		margin-top: 20px;
	}

	.transcript {
		margin-top: 25px;

		.transcript-header {
			display: flex;
			align-items: center;
			height: 60px;

			.carat {
				transform: rotate(180deg);
				transition: transform 0.3s;
				margin-left: 20px;
			}
		}

		.transcript-body {
			display: none;
			padding: 10px 0;
		}

		&.transcript-open {
			.transcript-body {
				display: block;
			}

			.transcript-header {
				.carat {
					transform: rotate(0deg);
				}
			}
		}
	}

	.previous-resources {
		ul {
			li {
				margin-bottom: 20px;
			}
		}
	}

	@media print {
		.page-title {
			display: none;
		}

		.aspect-ratio-box {
			.video {
				display: none;
			}
		}

		.main {
			max-width: initial;
		}

		.actions {
			display: none;
		}

		aside {
			display: none;
		}

		.transcript {
			.transcript-header {
				height: 40px;

				img {
					display: none;
				}
			}

			.transcript-body {
				display: block;
				columns: 2;
			}
		}

		.previous-resources {
			display: none;
		}
	}
}

@media screen and (width >= 1100px) {
	.resource {
		.resource-content {
			justify-content: space-between;

			.main {
				.title-container {
					display: flex;

					.actions {
						margin-top: 0;
					}
				}
			}

			aside {
				border-left: 1px solid rgb(var(--rs-color-brand-grey-light));
				padding-left: 20px;
				width: auto;
			}
		}
	}
}

@media screen and (width >= 1200px) {
	.resource {
		.video-container {
			margin-top: 40px;
		}
	}
}
</style>

<style lang="scss">
.resource-single-container .resource-edit {
	top: 8px !important;
}

@media (width >= 1024px) {
	body .resource-video + .related {
		max-width: 640px;
	}
}
</style>
